/**
 * API client.
 *
 * Same-origin, so the session cookie travels automatically and there is no token in localStorage
 * for an XSS to steal. The only manual work is echoing the CSRF cookie back as a header on
 * mutations, which is the other half of the double-submit check the server enforces.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** A number collision on create; the UI shows which number clashed. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

/**
 * Exported because file uploads bypass apiFetch: FormData must reach fetch untouched, and
 * JSON-encoding it would corrupt the body.
 */
export function csrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)klink_csrf=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

export async function apiFetch<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {};

  if (options.body !== undefined) headers['content-type'] = 'application/json';

  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const token = csrfToken();
    if (token) headers['x-csrf-token'] = token;
  }

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const body = parsed as { error?: string; issues?: Array<{ path: string; message: string }> };
    // Surface the first field-level validation message rather than a generic "validation failed",
    // which tells the admin nothing about which field is wrong.
    const detail = body?.issues?.[0]
      ? `${body.issues[0].path}: ${body.issues[0].message}`
      : (body?.error ?? `request failed (${response.status})`);
    throw new ApiError(response.status, detail, parsed);
  }

  return (parsed as { data: T })?.data ?? (parsed as T);
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiFetch<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiFetch<T>(path, { method: 'PUT', body }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};

// ---- typed resources -------------------------------------------------------------------------

export interface Me {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'viewer';
  /** Whether this account has an authenticator app enrolled. Never the secret. */
  totpEnabled?: boolean;
}

export interface Extension {
  id: string;
  number: string;
  name: string;
  email: string | null;
  sipEnabled: boolean;
  webrtcEnabled: boolean;
  voicemailEnabled: boolean;
  ringTimeout: number;
  callLimit: number;
  presence: string;
  cfAlways: string | null;
  cfBusy: string | null;
  cfNoAnswer: string | null;
  pickupGroups: string[];
  disabledAt: string | null;
  recordingMode: 'never' | 'always' | 'on_demand';
  outboundCidNumber: string | null;
  outboundCidName: string | null;
  codecs: string[];
  language: string;
}

export interface CreatedExtension {
  id: string;
  number: string;
  credentials: {
    sip: { username: string; password: string } | null;
    webrtc: { username: string; password: string } | null;
  };
  warning: string;
}

export type TrunkMode = 'register' | 'ip_auth' | 'account';

export interface Trunk {
  id: string;
  name: string;
  mode: TrunkMode;
  transport: 'udp' | 'tcp' | 'tls';
  serverHost: string;
  serverPort: number;
  outboundProxy: string | null;
  fromUser: string | null;
  fromDomain: string | null;
  authUsername: string | null;
  registerEnabled: boolean;
  registrationExpiry: number;
  /**
   * Live outbound registration state from Asterisk, joined onto the list response.
   *
   * Null for a trunk that does not register — which is not the same as "we could not find out",
   * and the two must not render alike.
   */
  registration?: string | null;
  nextAttempt?: string | null;
  /** Round trip to the provider, from Asterisk's qualify. */
  latencyMs?: number | null;
  matchIps: string[] | null;
  qualifyFrequency: number;
  maxChannels: number | null;
  codecs: string[];
  dtmfMode: 'rfc4733' | 'inband' | 'info' | 'auto';
  mediaEncryption: 'no' | 'sdes' | 'dtls';
  sendRpid: boolean;
  sendPai: boolean;
  trustIdInbound: boolean;
  t38Support: boolean;
  inbandProgress: boolean;
  rtpKeepalive: number;
  allowTransfer: boolean;
  failoverCodes: number[];
  callRestriction: 'outbound' | 'all';
  dids: string[];
  outboundCallerId: string | null;
  isActive: boolean;
}

export interface TrunkDod {
  id: string;
  dodNumber: string;
  extensionId: string | null;
}

/** The trunk editor's view: everything above, plus whether a password is already stored. */
export interface TrunkDetail extends Trunk {
  hasAuthPassword: boolean;
  dods: TrunkDod[];
}

export interface CallRecord {
  id: string;
  callId: string;
  direction: 'inbound' | 'outbound' | 'internal';
  fromNumber: string | null;
  toNumber: string | null;
  startedAt: string;
  durationSec: number | null;
  billsec: number | null;
  disposition: string | null;
}

export interface DestinationOption {
  type: string;
  id: string;
  label: string;
  number: string | null;
}

export interface SystemStatus {
  ami: { connected: boolean };
  config: {
    version: number;
    status: string;
    reloads: string[] | null;
    appliedAt: string | null;
    error: string | null;
    /** Asterisk is running an older config than the database describes, after a rollback. */
    drift: boolean;
  } | null;
}
